<template>
  <v-card>
    <v-form @submit="onSubmit">
      <v-toolbar flat dark color="primary" height="100">
        <v-col>
          <v-card-text>{{ this.dateSelected }}</v-card-text>
        </v-col>
        <v-col>
          <v-card-text>Add Project Time</v-card-text>
        </v-col>
      </v-toolbar>
      <v-container>
        <v-row class="ml-2 mb-2">Project</v-row>
        <v-autocomplete
          outlined
          autofocus
          clearable
          auto-select-first
          v-model="form.project_id"
          :items="projects"
          item-text="text"
          item-value="value"
          placeholder="Search project…"
          :menu-props="{maxHeight: 320}"
        />
        <v-row class="ml-2 mb-2">Task Description</v-row>
        <v-textarea
          outlined
          v-model="form.task_description"
          placeholder="Enter Description"
        >
        </v-textarea>
        <br />
        <v-row class="ml-2 mb-2">Hours in [HH:MM]</v-row>
        <v-text-field
          outlined
          v-model="form.hours"
          pattern="[0-9!:.]{0,5}"
          v-if="this.date === this.dateToday"
          placeholder="Enter Time in [00:00] HH:MM Format"
        >
        </v-text-field>
        <v-text-field
          outlined
          v-model="form.hours"
          placeholder="Enter Time in [00:00] HH:MM Format"
          v-else
          required
        >
        </v-text-field>
        <br />
        <v-card-actions>
          <v-btn color="success" type="submit" v-if="showStartTimerButton"
            >Start Timer</v-btn
          >
          <v-btn color="success" type="submit" v-else>Save</v-btn>
          <v-btn color="secondary" @click="closeModal">Cancel</v-btn>
        </v-card-actions>
      </v-container>
    </v-form>
  </v-card>
</template>
<script>
import {mapActions, mapGetters} from "vuex";
import {removeEmptyLines, strIs} from "@/Utilites/helper";
import {
  convertHMSFormatInputToHours,
  customFormatDate,
  GetCurrentTimeUtc,
  UpdateCurrentTimerData,
  today,
} from "@/Utilites/timerHelper";
import {isTimerV1Enabled} from "@/config/timerV1Config";
import {confirmTimerStartOnNonToday} from "@/services/timerV1Mapper";
import {resolveTimerUserId} from "@/services/timerActor";
import DEFAULT_CONSTANTS from "@/Utilites/constants";

function isZeroOrEmptyHours(hours) {
  if (!strIs(hours)) return true;
  const s = String(hours).trim();
  if (s === "0" || s === "0:0" || s === "0:00" || s === "00:00") return true;
  const n = convertHMSFormatInputToHours(hours);
  return n === 0 || Number.isNaN(n);
}

function normalizeFormHoursInput(hours) {
  if (!strIs(hours) || !String(hours).trim()) return 0;
  const parsed = convertHMSFormatInputToHours(hours);
  const n = Number(parsed);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : 0;
}

export default {
  name: "CreateTimerForm",
  props: ["date", "modalId"],
  data() {
    return {
      form: {
        user_id: "",
        project_id: null,
        hours: "00:00",
        hours_with_timer: "00:00",
        task_description: "",
        timer_log_date: "",
        timer_started_at: "",
        timer_stopped_at: "",
        timer_initiated_at: "",
        is_submitted: true,
        log_description: "",
        log_date: "",
        working_hours: "",
      },
      dateSelected: "",
      dateToday: "",
      projects: [],
    };
  },
  computed: {
    ...mapGetters([
      "getProjectNameList",
      "userProfileData",
      "getCurrentActiveTimer",
    ]),
    shouldOfferStartTimer() {
      return isZeroOrEmptyHours(this.form.hours);
    },
    showStartTimerButton() {
      if (isTimerV1Enabled()) {
        return this.shouldOfferStartTimer;
      }
      return this.date === this.dateToday && this.shouldOfferStartTimer;
    },
  },
  mounted() {
    this.loadData();
    this.dateSelected = customFormatDate(this.date, "LL");
    this.dateToday = today();
  },
  methods: {
    ...mapActions([
      "CREATE_TIMER_REQUEST",
      "UPDATE_TIMER_REQUEST",
      "TIMER_V1_START",
      "closeLoading",
      "showLoadingProcess",
      "showSnackBar",
    ]),
    projectSelected() {
      const id = this.form.project_id;
      return id != null && id !== "";
    },
    async onSubmit(evt) {
      evt.preventDefault();
      if (!this.projectSelected()) {
        this.showSnackBar({
          text: "Choose a project (type to search).",
          color: DEFAULT_CONSTANTS.error,
        });
        return;
      }
      const emptyHours = isZeroOrEmptyHours(this.form.hours);
      const useStart =
        emptyHours && (isTimerV1Enabled() || this.date === this.dateToday);
      if (useStart) {
        const started = await this.startTimer();
        if (started) {
          this.$emit("click-to-close");
        }
      } else {
        await this.saveTimer();
        this.$emit("click-to-close");
      }
    },
    loadData() {
      this.projects = (this.getProjectNameList || [])
        .filter((element) => element != null)
        .map((element) => ({
          value: element.id,
          text: element.project_name,
        }))
        .sort((a, b) =>
          String(a.text).localeCompare(String(b.text), undefined, {
            sensitivity: "base",
          })
        );
    },
    async startTimer() {
      const logDate = this.date;
      if (!confirmTimerStartOnNonToday(logDate)) {
        return false;
      }
      if (logDate && logDate !== this.dateToday) {
        this.showSnackBar({
          text: `Timer running on ${logDate} (not today).`,
          color: DEFAULT_CONSTANTS.warn,
        });
      }
      await this.showLoadingProcess();
      try {
        const uid = resolveTimerUserId(this.userProfileData);
        if (uid == null) {
          throw new Error(
            "Signed-in user id missing; log out and log in again."
          );
        }
        this.form.user_id = uid;
        this.form.hours_with_timer = 0;
        this.form.hours = 0;
        this.form.working_hours = 0;
        this.form.timer_log_date = this.date;
        this.form.task_description = removeEmptyLines(
          this.form.task_description
        );
        if (this.form.task_description === "") {
          this.form.task_description = "<Please Enter a Description Here>";
        }
        this.form.log_description = this.form.task_description;
        this.form.log_date = this.form.timer_log_date;

        if (isTimerV1Enabled()) {
          this.form.timer_started_at = null;
          this.form.timer_initiated_at = null;
          this.form.timer_stopped_at = null;
          this.closeModal();
          const created = await this.CREATE_TIMER_REQUEST({...this.form});
          if (created && created.id != null) {
            await this.TIMER_V1_START({
              project_id: this.form.project_id,
              task_description: this.form.task_description,
              legacy_timer_log_id: created.id,
              base_hours: 0,
              timer_log_date: this.date,
            });
          }
          return true;
        }

        this.form.timer_started_at = GetCurrentTimeUtc();
        this.form.timer_initiated_at = this.form.timer_started_at;
        this.form.timer_stopped_at = null;
        let currentTimer = {...this.getCurrentActiveTimer};
        if (currentTimer.id !== undefined) {
          UpdateCurrentTimerData(currentTimer);
          await this.UPDATE_TIMER_REQUEST({...currentTimer});
        }
        this.closeModal();
        await this.CREATE_TIMER_REQUEST({...this.form});
        return true;
      } catch (err) {
        console.log(JSON.stringify(err));
        return false;
      } finally {
        await this.closeLoading();
      }
    },
    async saveTimer() {
      await this.showLoadingProcess();
      const uid = resolveTimerUserId(this.userProfileData);
      if (uid == null) {
        throw new Error("Signed-in user id missing; log out and log in again.");
      }
      this.form.user_id = uid;
      this.form.hours_with_timer = normalizeFormHoursInput(this.form.hours);
      this.form.hours = this.form.hours_with_timer;
      this.form.timer_log_date = this.date;
      this.form.timer_started_at = null;
      this.form.timer_initiated_at = null;
      this.form.timer_stopped_at = null;
      this.form.working_hours = this.form.hours_with_timer;
      this.form.task_description = removeEmptyLines(this.form.task_description);
      this.form.log_description = this.form.task_description;
      this.form.log_date = this.form.timer_log_date;
      try {
        this.closeModal();
        await this.CREATE_TIMER_REQUEST({...this.form});
      } catch (err) {
        console.log(JSON.stringify(err));
      } finally {
        await this.closeLoading();
      }
    },

    closeModal() {
      this.$emit("click-to-close");
    },
  },
};
</script>
